import { useLocation, useNavigate, Link } from "react-router-dom";
import { scrollToHash } from "@/lib/scroll";
import { setInquiryPrefill } from "@/lib/inquiryPrefill";

const SOCIALS = [
  { label: "The Danielle Edit", handle: "@danielleedit.studio", href: "https://www.instagram.com/danielleedit.studio/", testId: "footer-social-de" },
  { label: "Founder", handle: "@itsdaniellenichol", href: "https://www.instagram.com/itsdaniellenichol/", testId: "footer-social-founder" },
  { label: "The RLMG", handle: "@the_rlmg", href: "https://www.instagram.com/the_rlmg/", testId: "footer-social-rlmg" },
];

const COMPANIES = [
  { label: "ASNHVN Group", href: "https://asnhvngroup.com", testId: "footer-company-asnhvn" },
  { label: "ANCR Ecosystem", href: "https://ancrecosystem.com", testId: "footer-company-ancr" },
];

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const goInquire = () => {
    const projectTitle = document.querySelector("[data-project-title]")?.getAttribute("data-project-title");
    if (projectTitle) setInquiryPrefill(projectTitle);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToHash("#inquire"), 400);
    } else {
      setTimeout(() => scrollToHash("#inquire"), 60);
    }
  };

  return (
  <footer
    data-testid="footer"
    className="relative overflow-hidden border-t border-[#f6f0e6]/10 text-[#f6f0e6] px-6 md:px-12 lg:px-24 pt-20 pb-10"
    style={{
      background:
        "radial-gradient(52% 46% at 50% 0%, rgba(130,24,150,0.24) 0%, transparent 64%), radial-gradient(52% 46% at 50% 100%, rgba(140,24,150,0.28) 0%, transparent 66%), linear-gradient(178deg, #0e0110 0%, #09000c 100%)",
    }}
  >
    {/* Approved purple chrome-ribbon bands — top and bottom edges */}
    <img
      src="/brand/footer-purple-ribbons.webp"
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 w-full h-full object-cover"
    />
    <div className="relative z-10">
      {/* Iridescent DE emblem watermark — faint, closing the page */}
      <img
        src="/brand/de-emblem-iridescent.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 md:right-24 -bottom-16 w-[240px] md:w-[340px] opacity-[0.08] select-none"
      />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
        <div>
          <p className="font-serif uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
            The Danielle<br />
            <span className="italic text-chrome-lavender">Edit</span>
          </p>
          <p data-testid="parent-lockup" className="mt-6 text-[10px] uppercase tracking-[0.34em] text-[#f6f0e6]/50">
            by A Stephens Danielle Co.
          </p>
        </div>
        <div className="md:text-right">
          <button
            data-testid="footer-cta"
            onClick={goInquire}
            className="group relative overflow-hidden bg-[#f6f0e6] text-[#160a17] text-[11px] uppercase tracking-[0.3em] rounded-full px-8 py-3.5 transition-transform duration-300 hover:scale-[1.03]"
          >
            <span className="relative z-10">Start Your Project</span>
            <span
              aria-hidden="true"
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: "linear-gradient(100deg, #e3c2bd, #c9a3e0, #b3a3d4, #e3c2bd)" }}
            />
          </button>
          <p className="mt-6">
            <a
              data-testid="footer-email-link"
              href="mailto:dani@danielleedit.com"
              className="font-serif italic text-lg md:text-xl border-b border-[#f6f0e6]/30 hover:border-[#e3c2bd] hover:text-[#e3c2bd] transition-colors duration-300 pb-1"
            >
              dani@danielleedit.com
            </a>
          </p>
          <p className="mt-4 text-sm tracking-[0.14em] text-[#f6f0e6]/60">
            <a data-testid="footer-phone-link" href="tel:+12246630427" className="hover:text-[#e3c2bd] transition-colors duration-300">
              224.663.0427
            </a>
            <span className="mx-3 text-[#f6f0e6]/30">·</span>
            <span>danielleedit.com</span>
          </p>
        </div>
      </div>

      {/* Secondary links */}
      <div className="mt-14 pt-8 border-t border-[#f6f0e6]/15 grid grid-cols-1 sm:grid-cols-2 gap-10 md:max-w-3xl">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#f6f0e6]/50 mb-4">On Instagram</p>
          <ul className="space-y-2.5">
            {SOCIALS.map((s) => (
              <li key={s.href}>
                <a
                  data-testid={s.testId}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-xs tracking-[0.08em] text-[#f6f0e6]/70 hover:text-[#e3c2bd] transition-colors duration-300"
                >
                  {s.label}
                  <span className="text-[#f6f0e6]/40 group-hover:text-[#e3c2bd]/80 transition-colors duration-300">
                    {" "}— {s.handle}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#f6f0e6]/50 mb-4">Explore Our Companies</p>
          <ul className="space-y-2.5">
            {COMPANIES.map((c) => (
              <li key={c.href}>
                <a
                  data-testid={c.testId}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs tracking-[0.08em] text-[#f6f0e6]/70 hover:text-[#e3c2bd] transition-colors duration-300"
                >
                  {c.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>

    <div className="relative z-10 mt-14 pt-6 border-t border-[#f6f0e6]/15 flex flex-col md:flex-row justify-between gap-3 text-[10px] uppercase tracking-[0.28em] text-[#f6f0e6]/40">
      <span className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span>© MMXXVI The Danielle Edit</span>
        <Link data-testid="footer-link-privacy" to="/privacy" className="hover:text-[#e3c2bd] transition-colors duration-300">
          Privacy Policy
        </Link>
        <Link data-testid="footer-link-terms" to="/terms" className="hover:text-[#e3c2bd] transition-colors duration-300">
          Terms of Use
        </Link>
      </span>
      <span>Series 01 — In the edit</span>
    </div>
  </footer>
  );
};
